<template>
  <div class="w-full p-6 bg-white rounded-lg shadow">
    <h4 class="text-base font-semibold">{{ title }}</h4>
    <p class="text-sm text-grey">{{ description }}</p>
    <div class="mt-4">
      <button class="sk-btn sk-btn-primary btn-border rounded-lg" @click="checkFacilitiesData">
        {{ buttonLabel }}
      </button>
    </div>
    <SKModal ref="checkDataModal" :title="modalTitle" closeButtonKey="apps.modals.cancel" actionButtonsPosition="center">
      <template #body>
        <div class="min-w-[320px]">
          <div v-if="modalWarnings.length" class="mb-4">
            <div v-for="(warning, index) in modalWarnings" :key="index" class="text-danger text-sm">
              {{ warning }}
            </div>
          </div>

          <div v-for="(section, index) in modalSections" :key="index" class="mb-4">
            <h5 class="text-sm font-semibold mb-2">{{ section.title }}</h5>
            <div v-if="section.rows.length === 0" class="text-sm text-grey">{{ $t('widgets.modal.no_data') }}</div>
            <div
              v-for="(row, rowIndex) in section.rows"
              :key="rowIndex"
              class="flex items-center justify-between py-1"
            >
              <div class="flex items-center gap-2">
                <img v-if="row.icon" :src="row.icon" class="w-5 h-5" />
                <span class="text-sm">{{ row.label }}</span>
              </div>
              <span class="text-sm font-medium">{{ row.value }}</span>
            </div>
          </div>

          <div v-if="modalNotes.length" class="text-xs text-grey">
            <div v-for="(note, index) in modalNotes" :key="index">{{ note }}</div>
          </div>
        </div>
      </template>
    </SKModal>
  </div>
</template>

<script>
import { getFacilityDifficulty, getFormatInSelectFacilityDifficulty } from '@/modules/Resorts/Helpers/facilityHelper'
import loader from '@/modules/Shared/Mixins/loader'
import SKModal from '@/modules/Shared/Components/modal/SKModal.vue'
import fetchFacilitiesMixin from './fetchFacilitiesMixin'
import checkFacilitiesModalMixin from './checkFacilitiesModalMixin'

export default {
  name: 'CheckFacilitiesDifficultyPanel',
  mixins: [loader, fetchFacilitiesMixin, checkFacilitiesModalMixin],
  components: {
    SKModal
  },
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    buttonLabel: {
      type: String,
      required: true
    },
    panelType: {
      type: String,
      required: true
    },
    selectedResortUuid: {
      type: String,
      required: true
    },
    resortInfo: {
      type: Object,
      default: null
    }
  },
  methods: {
    buildFacilitiesRows(facilities) {
      const difficultiesOrder = ['all', 0, 1, 6, 2, 3, 4, 5]
      const difficultyCounts = difficultiesOrder.reduce((acc, code) => {
        acc[code] = { open: 0, total: 0 }
        return acc
      }, {})

      facilities.forEach(facility => {
        const code = this.normalizeDifficultyCode(facility.difficulty)
        if (code === null) {
          return
        }

        difficultyCounts[code].total += 1
        difficultyCounts.all.total += 1
        if (this.isFacilityOpen(facility)) {
          difficultyCounts[code].open += 1
          difficultyCounts.all.open += 1
        }
      })

      const iconMap = this.getDifficultyIcons()
      return difficultiesOrder.map(code => {
        return {
          label: (iconMap[code] && iconMap[code].label) || this.$t('widgets.modal.labels.all'),
          value: `${ difficultyCounts[code].open} / ${ difficultyCounts[code].total}`,
          icon: iconMap[code] ? iconMap[code].icon : null
        }
      }).filter(row => row.value !== '0 / 0')
    },
    normalizeDifficultyCode(difficulty) {
      const formatted = getFormatInSelectFacilityDifficulty(difficulty, this.getDifficultyReference())
      const value = (formatted && formatted.value) || difficulty
      const mapping = {
        novice: 0,
        easy: 1,
        'intermediate advanced': 6,
        intermediate: 2,
        advanced: 3,
        expert: 4,
        freeride: 5,
        extreme: 6,
        low: 0,
        medium: 1,
        hard: 2,
        very_hard: 3
      }
      return Object.prototype.hasOwnProperty.call(mapping, value) ? mapping[value] : null
    },
    getDifficultyReference() {
      return this.resortInfo ? this.resortInfo.slope_difficulty_referencesystem : null
    },
    getDifficultyIcons() {
      const reference = this.getDifficultyReference()
      const options = getFacilityDifficulty(reference)
      const mappingByValue = options.reduce((acc, option) => {
        acc[option.value] = { label: option.label, icon: option.icon }
        return acc
      }, {})
      const valueByCode = {
        0: 'novice',
        1: 'easy',
        6: 'intermediate advanced',
        2: 'intermediate',
        3: 'advanced',
        4: 'expert',
        5: 'freeride'
      }
      return Object.keys(valueByCode).reduce((acc, code) => {
        const value = valueByCode[code]
        acc[code] = mappingByValue[value] || { label: value, icon: null }
        return acc
      }, {})
    },
    facilityTitle(type) {
      switch (type) {
        case 'slope':
          return this.$t('widgets.modal.facilities.slopes')
        case 'bikepark-trail':
          return this.$t('widgets.modal.facilities.bikepark')
        default:
          return this.$t('widgets.modal.facilities.default')
      }
    }
  }
}
</script>
